/**
 * Atlas Tea Co. — Palate Profile Finder
 * assets/atlas-palate-profile.js
 *
 * A 3-question finder that routes deterministically to one of three fixed
 * sampler packs. Works inline or as a modal popup. Email capture is
 * Klaviyo-direct (client-side, public IDs only, no private key):
 *
 *   - On email submit  → Klaviyo client Subscriptions API (list opt-in with
 *                         consent + palate properties on the profile).
 *   - On result shown  → Klaviyo `track` event + `identify` with the palate
 *                         properties against the browser/cookie profile, for
 *                         both the submit and skip paths. Skip = no list.
 *
 * No Shopify customer is created. Independent of the Crystal Ball section.
 *
 * Custom element: <atlas-palate-finder>
 */

(function () {
  'use strict';

  /* ── Klaviyo client API revision (bump as needed) ── */
  var KLAVIYO_REVISION = '2024-10-15';

  /* ── Questions: hardcoded copy + routing value keys ── */
  var QUESTIONS = [
    {
      id: 'tea_experience',
      question: 'How well do you know loose-leaf tea?',
      options: [
        { value: 'new',       label: 'New to it' },
        { value: 'sometimes', label: 'I drink it now and then' },
        { value: 'into_it',   label: 'I\'m well into it' }
      ]
    },
    {
      id: 'tea_style',
      question: 'What do you usually prefer in your cup?',
      options: [
        { value: 'strong_milk', label: 'Strong and full, usually with milk' },
        { value: 'clean_light', label: 'Clean and light' },
        { value: 'taste_range', label: 'I like a bit of everything' },
        { value: 'not_sure',    label: 'Not sure yet' }
      ]
    },
    {
      id: 'current_drink',
      question: "What's in your cup most days right now?",
      options: [
        { value: 'coffee',       label: 'Coffee' },
        { value: 'supermarket',  label: 'Supermarket tea bags' },
        { value: 'loose_leaf',   label: 'Loose-leaf already' },
        { value: 'green_herbal', label: 'Green or herbal' }
      ]
    }
  ];

  /* ── Routing: Q2 primary, Q3 tiebreaker for "not sure" ── */
  function routeToSampler(answers) {
    switch (answers.tea_style) {
      case 'strong_milk': return 'breakfast';
      case 'clean_light': return 'delicate';
      case 'taste_range': return 'range';
      case 'not_sure':
        switch (answers.current_drink) {
          case 'coffee':
          case 'supermarket':  return 'breakfast';
          case 'loose_leaf':   return 'range';
          case 'green_herbal': return 'delicate';
          default:             return 'range';
        }
      default: return 'range';
    }
  }

  /* ════════════════════════════════════════════════════════
     Cookie helpers (local — no theme dependency)
     ════════════════════════════════════════════════════════ */

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + d.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  function getCookie(name) {
    var match = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return match ? decodeURIComponent(match[1]) : '';
  }

  /* ════════════════════════════════════════════════════════
     Klaviyo helpers (public client-side)
     ════════════════════════════════════════════════════════ */

  function klaviyoIdentify(props) {
    window.klaviyo = window.klaviyo || [];
    try {
      if (typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify(props);
      } else {
        window.klaviyo.push(['identify', props]);
      }
    } catch (e) { /* non-fatal */ }
  }

  function klaviyoTrack(eventName, props) {
    window.klaviyo = window.klaviyo || [];
    try {
      if (typeof window.klaviyo.track === 'function') {
        window.klaviyo.track(eventName, props);
      } else {
        window.klaviyo.push(['track', eventName, props]);
      }
    } catch (e) { /* non-fatal */ }
  }

  /* Resolve whether the browser is an already-identified Klaviyo profile.
     Prefers the onsite isIdentified() promise; falls back to __kla_id. */
  function klaviyoIsIdentified() {
    return new Promise(function (resolve) {
      try {
        if (window.klaviyo && typeof window.klaviyo.isIdentified === 'function') {
          window.klaviyo.isIdentified()
            .then(function (v) { resolve(!!v); })
            .catch(function () { resolve(cookieIdentified()); });
          return;
        }
      } catch (e) { /* fall through */ }
      resolve(cookieIdentified());
    });
  }

  function cookieIdentified() {
    var raw = getCookie('__kla_id');
    if (!raw) return false;
    try {
      var decoded = JSON.parse(atob(raw));
      return !!(decoded && decoded.$email);
    } catch (e) {
      return false;
    }
  }

  /* Subscribe via the client Subscriptions API (list opt-in + properties). */
  function klaviyoSubscribe(companyId, listId, email, properties) {
    var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' +
      encodeURIComponent(companyId);

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Palate profile finder',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: properties
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(payload)
    });
  }

  /* ════════════════════════════════════════════════════════
     Global manual-trigger delegation (one modal per site)
     ════════════════════════════════════════════════════════ */

  var modalInstances = [];
  var globalBound = false;

  function bindGlobalManualTriggers() {
    if (globalBound) return;
    globalBound = true;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-atlas-palate-open], a[href$="#atlas-palate-finder"]');
      if (!trigger || !modalInstances.length) return;
      var instance = modalInstances[0];
      if (instance.cfg.triggerManual === 'false') return;
      e.preventDefault();
      instance.openModal('manual');
    });
  }

  /* ════════════════════════════════════════════════════════
     Custom element
     ════════════════════════════════════════════════════════ */

  class AtlasPalateFinder extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.cfg = this.dataset;
      this.isModal = this.cfg.displayMode === 'modal';
      this.designMode = this.cfg.designMode === 'true';

      this.root = this.querySelector('.atlas-pp');
      if (!this.root) return;

      this.screens = {};
      var nodes = this.root.querySelectorAll('[data-pp-screen]');
      for (var i = 0; i < nodes.length; i++) {
        this.screens[nodes[i].getAttribute('data-pp-screen')] = nodes[i];
      }

      this.progressWrap = this.root.querySelector('[data-pp-progress-wrap]');
      this.progressFill = this.root.querySelector('[data-pp-progress]');
      this.stepLabel    = this.root.querySelector('[data-pp-step-label]');
      this.questionEl   = this.root.querySelector('[data-pp-question]');
      this.hintEl       = this.root.querySelector('[data-pp-hint]');
      this.optionsEl    = this.root.querySelector('[data-pp-options]');
      this.backBtn      = this.root.querySelector('[data-pp-back]');
      this.stepCurrent  = this.root.querySelector('[data-pp-step-current]');
      this.stepTotal    = this.root.querySelector('[data-pp-step-total]');

      this.mediaImg = this.root.querySelector('[data-pp-media-img]');
      this.defaultMediaSrc = this.mediaImg ? (this.mediaImg.getAttribute('src') || '') : '';

      this.answers = {};
      this.currentQ = 0;
      this.subscribedEmail = null;
      this.advanceTimer = null;
      this._cleanupTriggers = null;

      if (this.stepTotal) this.stepTotal.textContent = QUESTIONS.length;

      this.bindEvents();

      if (this.isModal) {
        this.modal = this.querySelector('[data-pp-modal]');
        this.dialog = this.querySelector('.atlas-pp__modal-dialog');
        modalInstances.push(this);
        bindGlobalManualTriggers();
        this.setupTriggers();
      } else {
        this.showScreen('intro');
      }
    }

    /* ── Events ── */
    bindEvents() {
      var self = this;

      var startBtn = this.root.querySelector('[data-pp-start]');
      if (startBtn) startBtn.addEventListener('click', function () { self.start(); });

      if (this.backBtn) this.backBtn.addEventListener('click', function () { self.goBack(); });

      var emailForm = this.root.querySelector('[data-pp-email-form]');
      if (emailForm) emailForm.addEventListener('submit', function (e) { self.submitEmail(e); });

      var skipBtn = this.root.querySelector('[data-pp-skip]');
      if (skipBtn) skipBtn.addEventListener('click', function () { self.enterResult(); });

      var restartBtn = this.root.querySelector('[data-pp-restart]');
      if (restartBtn) restartBtn.addEventListener('click', function () { self.restart(); });

      if (this.isModal) {
        var closers = this.querySelectorAll('[data-pp-close]');
        for (var i = 0; i < closers.length; i++) {
          closers[i].addEventListener('click', function () { self.closeModal(); });
        }
        this._onKeydown = function (e) {
          if (e.key === 'Escape' && self.modal && self.modal.classList.contains('is-open')) {
            self.closeModal();
          }
        };
        document.addEventListener('keydown', this._onKeydown);
      }
    }

    /* Remove document-level listeners so the theme editor's repeated
       re-renders don't leak handlers (and stale instances aren't triggered). */
    disconnectedCallback() {
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
      if (this._onSectionSelect) document.removeEventListener('shopify:section:select', this._onSectionSelect);
      if (this._onSectionDeselect) document.removeEventListener('shopify:section:deselect', this._onSectionDeselect);
      if (this._onSectionLoad) document.removeEventListener('shopify:section:load', this._onSectionLoad);
      if (this._cleanupTriggers) { this._cleanupTriggers(); this._cleanupTriggers = null; }
      var idx = modalInstances.indexOf(this);
      if (idx !== -1) modalInstances.splice(idx, 1);
    }

    /* ── Screen management ── */
    showScreen(name) {
      var self = this;
      Object.keys(this.screens).forEach(function (key) {
        self.screens[key].classList.remove('atlas-pp__screen--active');
      });
      if (this.screens[name]) this.screens[name].classList.add('atlas-pp__screen--active');
      if (this.progressWrap) this.progressWrap.hidden = (name !== 'quiz');
    }

    /* ── Optional image side panel ── */
    setMedia(src) {
      if (!this.mediaImg) return;
      var next = src || this.defaultMediaSrc;
      if (next) {
        this.mediaImg.src = next;
        this.mediaImg.hidden = false;
      } else {
        this.mediaImg.removeAttribute('src');
        this.mediaImg.hidden = true;
      }
    }

    /* ── Quiz ── */
    start() {
      this.answers = {};
      this.currentQ = 0;
      this.subscribedEmail = null;
      this.setMedia('');
      this.showScreen('quiz');
      this.renderQuestion();
    }

    renderQuestion() {
      var self = this;
      var q = QUESTIONS[this.currentQ];

      this.stepLabel.textContent = 'Question ' + (this.currentQ + 1) + ' of ' + QUESTIONS.length;
      if (this.stepCurrent) this.stepCurrent.textContent = this.currentQ + 1;
      this.questionEl.textContent = q.question;

      if (this.hintEl) {
        if (q.hint) { this.hintEl.textContent = q.hint; this.hintEl.hidden = false; }
        else { this.hintEl.textContent = ''; this.hintEl.hidden = true; }
      }

      if (this.backBtn) this.backBtn.hidden = (this.currentQ === 0);

      if (this.progressFill) {
        this.progressFill.style.width = (((this.currentQ + 1) / QUESTIONS.length) * 100) + '%';
      }

      this.optionsEl.innerHTML = '';
      q.options.forEach(function (opt) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'atlas-pp__option';
        btn.textContent = opt.label;
        if (self.answers[q.id] === opt.value) btn.classList.add('atlas-pp__option--selected');
        btn.addEventListener('click', function () { self.selectOption(opt.value, btn); });
        self.optionsEl.appendChild(btn);
      });
    }

    selectOption(value, clickedBtn) {
      var self = this;
      var q = QUESTIONS[this.currentQ];
      this.answers[q.id] = value;

      var all = this.optionsEl.querySelectorAll('.atlas-pp__option');
      for (var i = 0; i < all.length; i++) all[i].classList.remove('atlas-pp__option--selected');
      clickedBtn.classList.add('atlas-pp__option--selected');

      clearTimeout(this.advanceTimer);
      this.advanceTimer = setTimeout(function () {
        if (self.currentQ < QUESTIONS.length - 1) {
          self.currentQ++;
          self.renderQuestion();
        } else {
          self.showScreen('email');
          self.focusFirstInput();
        }
      }, 340);
    }

    goBack() {
      if (this.currentQ > 0) {
        this.currentQ--;
        this.renderQuestion();
      }
    }

    focusFirstInput() {
      var input = this.root.querySelector('[data-pp-email-input]');
      if (input) { try { input.focus(); } catch (e) {} }
    }

    /* ── Email submit → Klaviyo subscribe ── */
    submitEmail(e) {
      e.preventDefault();
      var self = this;

      var input   = this.root.querySelector('[data-pp-email-input]');
      var submit  = this.root.querySelector('[data-pp-email-submit]');
      var errorEl = this.root.querySelector('[data-pp-email-error]');

      if (errorEl) { errorEl.textContent = ''; errorEl.classList.remove('is-visible'); }

      var email = input ? input.value.trim() : '';
      if (!email || (input && input.validity && !input.validity.valid)) {
        if (input) input.focus();
        return;
      }

      var companyId = this.cfg.companyId;
      var listId = this.cfg.listId;

      /* Without Klaviyo config we can't subscribe — still honour the flow and
         show the result rather than trapping the visitor. Don't claim a
         sign-up happened. */
      if (!companyId || !listId) {
        this.enterResult();
        return;
      }

      var originalLabel = submit ? submit.innerHTML : '';
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        submit.innerHTML = '<span aria-hidden="true">&hellip;</span>';
      }

      klaviyoSubscribe(companyId, listId, email, this.buildProperties())
        .then(function (res) {
          if (res && res.ok) {
            self.subscribedEmail = email;
            if (self.cfg.frequencyDays !== '0') setCookie('atlas_pp_done', '1', 365);
            self.enterResult();
          } else {
            self.showEmailError(submit, originalLabel, errorEl);
          }
        })
        .catch(function () {
          self.showEmailError(submit, originalLabel, errorEl);
        });
    }

    showEmailError(submit, originalLabel, errorEl) {
      if (submit) {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        submit.innerHTML = originalLabel;
      }
      if (errorEl) {
        errorEl.textContent = this.cfg.emailError || 'Something went wrong. Please try again.';
        errorEl.classList.add('is-visible');
      }
    }

    /* ── Result ── */
    buildProperties() {
      return {
        tea_experience: this.answers.tea_experience || '',
        tea_style: this.answers.tea_style || '',
        current_drink: this.answers.current_drink || '',
        recommended_sampler: routeToSampler(this.answers)
      };
    }

    enterResult() {
      var sampler = routeToSampler(this.answers);

      var matched = null;
      var outcomes = this.root.querySelectorAll('[data-pp-outcome]');
      for (var i = 0; i < outcomes.length; i++) {
        var isMatch = (outcomes[i].getAttribute('data-pp-outcome') === sampler);
        outcomes[i].hidden = !isMatch;
        if (isMatch) matched = outcomes[i];
      }

      if (matched) this.setMedia(matched.getAttribute('data-pp-image'));

      var doneEl = this.root.querySelector('[data-pp-email-success]');
      if (doneEl) doneEl.hidden = !this.subscribedEmail;

      /* Enrich the browser profile + fire the event (both submit + skip). */
      var props = this.buildProperties();
      var idPayload = {};
      Object.keys(props).forEach(function (k) { idPayload[k] = props[k]; });
      if (this.subscribedEmail) idPayload.email = this.subscribedEmail;
      klaviyoIdentify(idPayload);
      klaviyoTrack(this.cfg.eventName || 'Completed palate profile', props);

      this.showScreen('result');
      if (this.dialog) { try { this.dialog.scrollTop = 0; } catch (e) {} }
    }

    restart() {
      this.answers = {};
      this.currentQ = 0;
      this.subscribedEmail = null;

      var input = this.root.querySelector('[data-pp-email-input]');
      if (input) input.value = '';
      var errorEl = this.root.querySelector('[data-pp-email-error]');
      if (errorEl) { errorEl.textContent = ''; errorEl.classList.remove('is-visible'); }

      this.setMedia('');
      this.showScreen('intro');
    }

    /* ════════════════════════════════════════════════════════
       Modal — open / close / triggers / suppression
       ════════════════════════════════════════════════════════ */

    setupTriggers() {
      var self = this;

      /* Theme editor: keep the modal visible while editing. Reopen after each
         re-render (section:load) and on select; close on deselect. Never sets
         cookies or fires on the live site. */
      if (this.designMode && window.Shopify && Shopify.designMode) {
        var isThisSection = function (e) {
          return e.detail && e.detail.sectionId && self.id.indexOf(e.detail.sectionId) !== -1;
        };
        this._onSectionSelect = function (e) { if (isThisSection(e)) self.openModal('editor'); };
        this._onSectionLoad = function (e) { if (isThisSection(e)) self.openModal('editor'); };
        this._onSectionDeselect = function (e) { if (isThisSection(e)) self.closeModal(); };
        document.addEventListener('shopify:section:select', this._onSectionSelect);
        document.addEventListener('shopify:section:load', this._onSectionLoad);
        document.addEventListener('shopify:section:deselect', this._onSectionDeselect);
        return;
      }

      /* Live auto-open is gated on page permission + suppression. */
      if (this.cfg.allowAutoopen !== 'true') return;
      if (this.isSuppressed()) return;

      var wantsAuto = (
        this.cfg.triggerDelay === 'true' ||
        this.cfg.triggerExit === 'true' ||
        this.cfg.triggerScroll === 'true'
      );
      if (!wantsAuto) return;

      var maybeArm = function (known) {
        if (known && self.cfg.suppressIdentified !== 'false') return;
        self.armTriggers();
      };

      if (this.cfg.suppressIdentified !== 'false') {
        klaviyoIsIdentified().then(maybeArm);
      } else {
        maybeArm(false);
      }
    }

    isSuppressed() {
      if (getCookie('atlas_pp_done')) return true;
      if (this.cfg.frequencyDays !== '0' && getCookie('atlas_pp_seen')) return true;
      return false;
    }

    armTriggers() {
      var self = this;
      var fired = false;
      var delayTimer = null;

      function cleanup() {
        if (delayTimer) clearTimeout(delayTimer);
        window.removeEventListener('scroll', onScroll);
        document.removeEventListener('mouseout', onMouseOut);
      }

      function fire() {
        if (fired) return;
        fired = true;
        cleanup();
        self.openModal('auto');
      }

      function onScroll() {
        var docH = document.documentElement.scrollHeight - window.innerHeight;
        var pct = docH > 0 ? (window.scrollY / docH) * 100 : 0;
        if (pct >= parseFloat(self.cfg.scrollPercent || '45')) fire();
      }

      function onMouseOut(e) {
        if (!e.relatedTarget && e.clientY <= 0) fire();
      }

      if (this.cfg.triggerDelay === 'true') {
        delayTimer = setTimeout(fire, parseFloat(this.cfg.delaySeconds || '12') * 1000);
      }
      if (this.cfg.triggerScroll === 'true') {
        window.addEventListener('scroll', onScroll, { passive: true });
      }
      /* Exit intent is desktop-only (no meaningful mouseout on touch). */
      if (this.cfg.triggerExit === 'true' && !('ontouchstart' in window)) {
        document.addEventListener('mouseout', onMouseOut);
      }

      this._cleanupTriggers = cleanup;
    }

    openModal(reason) {
      if (!this.modal) return;

      if (this._cleanupTriggers) { this._cleanupTriggers(); this._cleanupTriggers = null; }

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('prevent-scroll');

      if (this.dialog) { try { this.dialog.focus(); } catch (e) {} }

      /* Auto/dismiss frequency cap — not for editor previews. */
      if (reason !== 'editor' && this.cfg.frequencyDays !== '0') {
        setCookie('atlas_pp_seen', '1', parseInt(this.cfg.frequencyDays || '7', 10));
      }
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('prevent-scroll');
    }
  }

  if (window.customElements && !window.customElements.get('atlas-palate-finder')) {
    window.customElements.define('atlas-palate-finder', AtlasPalateFinder);
  }
})();
